import { activityFetch as fetch } from '../../components/feedback/requestActivity'
import { useCallback, useEffect, useRef, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import DatabaseMigration from './DatabaseMigration'

const roles = { primary: '주 DB', replica: '복제 DB', unknown: '역할 미확인' }
const time = value => value && Number.isFinite(Date.parse(value)) ? new Date(value).toLocaleString('ko-KR') : '미확인'
export default function StackGresDatabase() {
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [now, setNow] = useState(Date.now())
  const inFlight=useRef(false)
  const load = useCallback(async () => {
    if(inFlight.current)return
    inFlight.current=true
    setLoading(true); setError('')
    const abort=new AbortController(),timeout=setTimeout(()=>abort.abort(),15000)
    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token')
      const r = await fetch('/api/orch/k8s/database-snapshot?environment=production', {
        credentials: 'include', signal:abort.signal, headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!r.ok) throw Error(r.status === 401 ? '로그인이 필요합니다.' : r.status === 403 ? '관리자 권한이 필요합니다.' : 'DB 운영 상태를 조회하지 못했습니다.')
      const result = await r.json()
      if (!Array.isArray(result.clusters)) throw Error('DB 운영 상태 응답을 확인할 수 없습니다.')
      setData(result); setNow(Date.now())
    } catch (e) { setError(e.name==='AbortError'?'DB 상태 조회 시간이 초과됐습니다.':e.message) } finally { clearTimeout(timeout);inFlight.current=false;setLoading(false) }
  }, [])
  useEffect(() => {
    load()
    const timer=setInterval(()=>setNow(Date.now()),15000)
    const refresh=setInterval(()=>{if(document.visibilityState!=='hidden')load()},30000)
    return()=>{clearInterval(timer);clearInterval(refresh)}
  }, [load])
  const age = now - Date.parse(data?.observedAt)
  const stale = Boolean(error) || !Number.isFinite(age) || age > 120000 || age < -30000
  return <div className="distributed-db sg-db">
    <div className="ddb-toolbar"><div><h2>PostgreSQL 운영</h2><p>StackGres의 DB, 복제본과 백업을 실제 클러스터에서 조회합니다.</p></div>
      <button onClick={load} disabled={loading}><RefreshCw size={15} />{loading ? '조회 중' : '새로고침'}</button></div>
    {error && <p role="alert" className="sg-notice">{error}</p>}
    {data && <p className="sg-observed">조회 {time(data.observedAt)} · 30초마다 자동 갱신{stale && ' · 재확인 필요'}</p>}
    {data && <DatabaseMigration data={data.migration} stale={stale} now={now} />}
    {data?.controllers?.length>0&&<details className="sg-controllers"><summary>관리 구성요소 · {stale?'미확인':`${data.controllers.filter(c=>c.healthy).length}/${data.controllers.length} 정상`}</summary>
      <ul>{data.controllers.map(c=><li key={c.name}><span>{({'stackgres-operator':'StackGres 운영 제어','stackgres-restapi':'StackGres 관리 API','keda-operator':'KEDA 확장 제어','keda-operator-metrics-apiserver':'KEDA 확장 지표','keda-admission-webhooks':'KEDA 설정 검증'})[c.name]||c.name}</span><span className={`sg-signal ${!stale&&c.healthy?'is-good':''}`}>{stale?'미확인':c.healthy?'정상':'확인 필요'}</span></li>)}</ul>
    </details>}
    {data?.clusters.length === 0 && <p className="sg-notice">등록된 StackGres 클러스터가 없습니다.</p>}
    {data?.clusters.map(cluster => <article className="sg-cluster" key={cluster.name}>
      <header><div><h3>{cluster.name}</h3><span>PostgreSQL {cluster.version || '버전 미확인'} · {cluster.stage === 'production' ? '운영' : cluster.stage === 'validation' ? '복원 검증용' : '용도 미확인'}</span></div>
        <span className={`sg-signal ${!stale && cluster.healthy ? 'is-good' : ''}`}>
          {stale ? '상태 미확인' : cluster.healthy ? 'DB 실행 정상' : '확인 필요'}</span></header>
      {cluster.stage === 'validation' && <p className="sg-notice">격리된 복원 검증 환경입니다. 서비스 DB 전환과 운영 백업 완료를 의미하지 않습니다.</p>}
      <dl className="sg-facts">
        <div><dt>준비된 Pod</dt><dd>{stale ? '미확인' : `${cluster.ready} / ${cluster.desired ?? '?'}`}</dd></div>
        <div><dt>서버 간 이중화</dt><dd>{stale ? '미확인' : cluster.physicalHighAvailability ? `${cluster.physicalNodes}대에 분산` : '미구성'}</dd></div>
        <div><dt>읽기 복제본 자동 확장</dt><dd>{stale ? '미확인' : cluster.autoscaling?.enabled ? cluster.autoscaling.active ? `${cluster.autoscaling.min}–${cluster.autoscaling.max} Pod` : '설정됨 · 동작 확인 필요' : '미구성'}</dd></div>
        <div><dt>운영 데이터 백업</dt><dd>{stale ? '미확인' : cluster.productionBackupCurrent ? '최근 백업 완료' : cluster.stage === 'validation' ? '검증 단계' : '최근 백업 확인 필요'}</dd></div>
      </dl>
      <div className="sg-members" aria-label="DB Pod 배치">
        {cluster.pods.map(pod => <div className="sg-member" key={pod.name}>
          <div><strong>{roles[pod.role] || roles.unknown}</strong><span className={`sg-signal ${!stale && pod.ready ? 'is-good' : ''}`}>{stale ? '미확인' : pod.ready ? '준비됨' : '준비 안 됨'}</span></div>
          <div><code>{pod.name}</code><span>서버 {pod.node || '미배치'}</span></div>
          <div>{pod.storage.map(v => <span key={v.name}>디스크 {v.capacity || '미확인'} · {v.phase === 'Bound' ? '연결됨' : '연결 확인 필요'}</span>)}<span>재시작 {pod.restarts}회</span></div>
        </div>)}
        {!cluster.pods.length && <p>배치된 DB Pod가 없습니다.</p>}
      </div>
      <footer>최근 완료 백업: {cluster.lastBackup ? `${time(cluster.lastBackup.storedAt)} · ${cluster.lastBackup.encrypted ? '암호화' : '암호화 미확인'}` : '없음'}</footer>
    </article>)}
    <p className="sg-footnote">자동 확장은 읽기 복제본을 늘립니다. 쓰기 부하는 주 DB 용량과 쿼리·데이터 분산 설계로 별도 관리합니다.</p>
  </div>
}
